import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Play, Plus, Trash2, Pencil, Star, Users2, ChevronDown, ChevronRight,
  CheckCircle2, AlertTriangle, Info, UserPlus, Layers, Scale, X, Copy,
} from 'lucide-react';
import { api } from '../api';
import { PageLoading, Modal, useToast, StatusPill, ROLE_LABEL, Confirm } from '../components/ui';

const APP_STATUS = {
  assigned: { cls: 'badge-green', label: 'Eingeteilt' },
  waitlist: { cls: 'badge-amber', label: 'Warteliste' },
  ineligible: { cls: 'badge-gray', label: 'Nicht einteilbar' },
};
function AppStatus({ s }) {
  const m = APP_STATUS[s];
  return m ? <span className={`badge ${m.cls}`}>{m.label}</span> : <span className="badge badge-gray">offen</span>;
}

function partWhen(p) {
  if (!p.start_time) return p.date || 'ohne Datum';
  if (p.end_date && p.end_date > p.date) return `${p.date} ${p.start_time} – ${p.end_date} ${p.end_time} (${p.hours} h)`;
  const nextDay = p.end_time && p.end_time <= p.start_time; // impliziter Mitternachtsfall
  return `${p.date || 'ohne Datum'} · ${p.start_time}–${p.end_time}${nextDay ? ' (+1 Tag)' : ''} (${p.hours} h)`;
}
function partRequirement(p) {
  const bits = [];
  if (p.requires_gf) bits.push('GF');
  if (p.requires_ma) bits.push(p.ma_requires_ce ? 'Ma (CE)' : 'Ma');
  const crew = Math.max(0, p.total_slots - (p.requires_gf ? 1 : 0) - (p.requires_ma ? 1 : 0));
  if (crew > 0) bits.push(`${crew}× Mannschaft${p.min_as ? ` (min. ${p.min_as} AS)` : ''}`);
  return bits.join(' · ');
}

export default function WatchDetail() {
  const { id } = useParams();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [manual, setManual] = useState(null); // {part}
  const [addPartFor, setAddPartFor] = useState(null); // Teil, zu dem Bewerber hinzugefügt werden
  const [openRank, setOpenRank] = useState({});
  const [textOpen, setTextOpen] = useState(false);
  const [relConfirm, setRelConfirm] = useState(null); // Freigabe-Rückfrage (Hinweistext)
  const [problems, setProblems] = useState([]);

  const load = () => Promise.all([
    api.get(`/watches/${id}`),
    api.get(`/watches/${id}/problems`).catch(() => ({ problems: [] })),
  ]).then(([d, pr]) => { setData(d); setProblems(pr.problems || []); });
  useEffect(() => { load(); api.get('/personnel').then((d) => setPeople(d.personnel.filter((p) => p.active))); /* eslint-disable-next-line */ }, [id]);
  if (!data) return <PageLoading />;

  const { watch, parts, applications, assignments, decision } = data;
  const multi = parts.length > 1;
  const partById = Object.fromEntries(parts.map((p) => [p.id, p]));
  const candidateIds = new Set(applications.map((a) => a.person_id));

  const runAssign = async () => {
    setBusy(true);
    try { const r = await api.post(`/watches/${id}/assign`); await load(); toast(`Einteilung erstellt: ${r.snapshot.summary.filledSlots}/${r.snapshot.summary.totalSlots} Plätze besetzt`); }
    catch (e) { toast(e.message, 'err'); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => { try { await api.put(`/watches/${id}/status`, { status }); load(); } catch (e) { toast(e.message, 'err'); } };
  const doRelease = async () => { try { await api.post(`/watches/${id}/release`); load(); toast('Einteilung freigegeben'); } catch (e) { toast(e.message, 'err'); } };
  const release = () => {
    const totalSlots = parts.reduce((s, p) => s + p.total_slots, 0);
    const open = totalSlots - assignments.length;
    const errs = problems.filter((p) => p.severity === 'error').length;
    const bits = [];
    if (open > 0) bits.push(`${open} Platz/Plätze offen`);
    if (errs > 0) bits.push(`${errs} Problem(e) im Feld „Probleme & Hinweise"`);
    if (bits.length) setRelConfirm(bits.join(' und ')); else doRelease();
  };
  const unrelease = async () => { try { await api.post(`/watches/${id}/unrelease`); load(); toast('Bearbeitung wieder geöffnet'); } catch (e) { toast(e.message, 'err'); } };
  const requestPublish = async () => {
    setBusy(true);
    try { const r = await api.post(`/watches/${id}/request-publish`); await load(); toast(`Freigabe-Mail verschickt (${r.type}, Anmeldeschluss ${r.deadline ? r.deadline.split('-').reverse().join('.') : '—'})`); }
    catch (e) { toast(e.message, 'err'); }
    finally { setBusy(false); }
  };
  const copyText = async () => {
    try { await navigator.clipboard.writeText(buildAssignmentText()); toast('In die Zwischenablage kopiert'); }
    catch { toast('Bitte den Text markieren und manuell kopieren', 'err'); }
  };
  const buildAssignmentText = () => {
    const fmt = (d) => (d ? d.split('-').reverse().join('.') : '');
    const unassigned = applications.filter((a) => !assignments.some((x) => x.person_id === a.person_id));
    const singleGf = !multi ? assignments.find((a) => a.role === 'GF') : null;
    const out = [];
    out.push(`Die Einteilung für die Sicherheitswache${watch.title ? ` „${watch.title}"` : ''} ist erfolgt.${singleGf ? ` Wachführer ist ${singleGf.personName}.` : ''}`);
    let thanks = 'Vielen Dank für die Meldungen!';
    if (unassigned.length > 0) thanks += ' Bitte meldet euch gerne auch für die kommenden Sicherheitswachen, so wird jeder und jede einmal zum Zug kommen!';
    out.push('', thanks, '', 'Die Einteilung ist wie folgt:', '');
    for (const p of parts) {
      const as = assignsByPart(p.id);
      if (!as.length) continue;
      if (multi) {
        const when = p.date ? ` (${fmt(p.date)}${p.start_time ? `, ${p.start_time}–${p.end_time} Uhr` : ''})` : '';
        out.push(`${p.label}${when}:`);
      }
      const role = (r) => as.filter((a) => a.role === r).map((a) => a.personName);
      const gf = role('GF'), ma = role('Ma'), crew = role('CREW');
      if (multi && gf.length) out.push(`Wachführer: ${gf.join(', ')}`);
      if (ma.length) out.push(`Maschinist: ${ma.join(', ')}`);
      if (crew.length) out.push(`Mannschaft: ${crew.join(', ')}`);
      out.push('');
    }
    out.push('Viel Spaß und beste Grüße');
    return out.join('\n');
  };
  const removeApp = async (aid) => { try { await api.del(`/applications/${aid}`); load(); toast('Bewerber entfernt'); } catch (e) { toast(e.message, 'err'); } };
  const removeAssign = async (asid) => { try { await api.del(`/assignments/${asid}`); load(); toast('Einteilung entfernt'); } catch (e) { toast(e.message, 'err'); } };
  const removeFromPart = async (partId, personId) => { try { await api.del(`/parts/${partId}/applicants/${personId}`); load(); } catch (e) { toast(e.message, 'err'); } };
  const togglePref = async (a, partId) => {
    const next = a.preferredPartIds.includes(partId) ? a.preferredPartIds.filter((x) => x !== partId) : [...a.preferredPartIds, partId];
    try { await api.put(`/applications/${a.id}`, { preferred_part_ids: next }); load(); } catch (e) { toast(e.message, 'err'); }
  };

  const assignsByPart = (pid) => assignments.filter((a) => a.part_id === pid);
  const applicantsForPart = (pid) => applications.filter((a) => a.availablePartIds.includes(pid));

  return (
    <>
      <Link to="/wachen" className="nav-link" style={{ color: 'var(--text-secondary)', padding: 0, marginBottom: 12, display: 'inline-flex' }}><ArrowLeft size={16} /> Alle Wachen</Link>
      <div className="page-head">
        <div>
          <h1>{watch.title}</h1>
          <div className="sub">{[watch.location, multi ? `${parts.length} Teile` : null, watch.description].filter(Boolean).join(' · ') || '—'}</div>
        </div>
        <div className="toolbar">
          <select value={watch.status} onChange={(e) => setStatus(e.target.value)} style={{ width: 'auto' }}>
            <option value="draft">Entwurf</option><option value="planned">Geplant</option><option value="assigned">Eingeteilt</option>
            <option value="completed">Abgeschlossen</option><option value="cancelled">Storniert</option>
          </select>
          <button className="btn btn-primary" onClick={runAssign} disabled={busy || applications.length === 0}><Play size={16} /> {busy ? 'Rechne…' : (assignments.length ? 'Neu berechnen (Vorschlag)' : 'Einteilung berechnen')}</button>
          {assignments.length > 0 && <button className="btn btn-secondary" onClick={() => setTextOpen(true)}><Copy size={16} /> Text für Mitteilung</button>}
        </div>
      </div>

      {/* FF-Agent-Freigabe: Status + Button */}
      {!['completed', 'cancelled'].includes(watch.status) && (() => {
        const ps = watch.publish_status;
        const dl = watch.registration_deadline ? watch.registration_deadline.split('-').reverse().join('.') : null;
        const dlTxt = dl ? ` · Anmeldeschluss ${dl}${watch.publish_short_notice ? ' (kurzfristig)' : ''}` : '';
        if (ps === 'approved') return (
          <div className="info-box" style={{ marginBottom: 16, background: 'var(--green-bg)', border: '1px solid #bfe6cc', color: 'var(--green)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span><CheckCircle2 size={15} style={{ verticalAlign: -3 }} /> Für FF‑Agent <strong>freigegeben</strong>{dlTxt}. Veröffentlichung folgt (nächster Schritt).</span>
            <button className="btn btn-secondary btn-sm" onClick={requestPublish} disabled={busy}>Freigabe-Mail erneut</button>
          </div>
        );
        if (ps === 'review') return (
          <div className="info-box" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span><Info size={15} style={{ verticalAlign: -3 }} /> Freigabe-Mail verschickt – warte auf deine Bestätigung per Mail{dlTxt}.</span>
            <button className="btn btn-secondary btn-sm" onClick={requestPublish} disabled={busy}>Erneut senden</button>
          </div>
        );
        if (ps === 'changes') return (
          <div className="info-box" style={{ marginBottom: 16, background: 'var(--amber-bg)', border: '1px solid var(--amber)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span><AlertTriangle size={15} style={{ verticalAlign: -3 }} /> Als „Änderungen notwendig" markiert. Wache anpassen und Freigabe erneut anfordern.</span>
            <button className="btn btn-primary btn-sm" onClick={requestPublish} disabled={busy}>Freigabe erneut anfordern</button>
          </div>
        );
        return (
          <div className="info-box" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span><Info size={15} style={{ verticalAlign: -3 }} /> Noch nicht für FF‑Agent freigegeben. „Freigabe anfordern" schickt dir die Übersichts-Mail mit Ankündigungstext + Anmeldeschluss zum Bestätigen.</span>
            <button className="btn btn-primary btn-sm" onClick={requestPublish} disabled={busy || !parts.some((p) => p.date)}>Freigabe anfordern</button>
          </div>
        );
      })()}

      {assignments.length > 0 && (watch.released ? (
        <div className="info-box" style={{ marginBottom: 16, background: 'var(--green-bg)', border: '1px solid #bfe6cc', color: 'var(--green)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span><CheckCircle2 size={15} style={{ verticalAlign: -3 }} /> Einteilung <strong>freigegeben</strong>{watch.released_at ? ` am ${watch.released_at}` : ''}.</span>
          <button className="btn btn-secondary btn-sm" onClick={unrelease}>Bearbeitung wieder öffnen</button>
        </div>
      ) : (
        <div className="info-box" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span><Info size={15} style={{ verticalAlign: -3 }} /> <strong>Entwurf</strong> – Vorschlag der Einteilung. Du kannst noch anpassen (manuell einteilen/entfernen, Plätze nachbesetzen) und gibst dann frei.</span>
          <button className="btn btn-primary btn-sm" onClick={release}>Freigeben</button>
        </div>
      ))}

      {/* Einteilung je Teil (volle Breite; „Teil"-Label nur bei mehrteiligen Wachen) */}
      <div style={{ marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {parts.map((p) => {
          const list = assignsByPart(p.id);
          const apps = applicantsForPart(p.id);
          const dpart = decision && decision.parts ? decision.parts.find((x) => x.part_id === p.id) : null;
          const openSlots = dpart ? dpart.slots.filter((s) => !s.filledBy) : [];
          const full = list.length >= p.total_slots;
          return (
            <div className="card card-pad" key={p.id}>
              <div className="flex between flex-wrap" style={{ gap: 8 }}>
                <div>
                  {multi && <div className="card-title"><Layers size={16} /> {p.label}</div>}
                  <div className="small muted">{partWhen(p)} · Bedarf: {partRequirement(p)}</div>
                </div>
                <span className={`badge ${full ? 'badge-green' : 'badge-amber'}`}>{list.length}/{p.total_slots} besetzt</span>
              </div>

              {multi && (
                <>
                  <div className="divider" />
                  <div className="flex between" style={{ marginBottom: 8 }}>
                    <div className="small" style={{ fontWeight: 600 }}><Users2 size={14} style={{ verticalAlign: -2 }} /> Bewerber für diesen Teil ({apps.length})</div>
                    <button className="btn btn-secondary btn-sm" onClick={() => setAddPartFor(p)}><Plus size={14} /> Bewerber</button>
                  </div>
                  {apps.length === 0 ? <div className="muted small">Noch keine Bewerber für diesen Teil.</div> : (
                    <div className="tag-list">
                      {apps.map((a) => {
                        const pref = a.preferredPartIds.includes(p.id);
                        const assignedHere = list.some((x) => x.person_id === a.person_id);
                        return (
                          <span key={a.id} className="applicant-chip" style={assignedHere ? { borderColor: 'var(--green)', background: 'var(--green-bg)' } : {}}>
                            <button className="chip-star" title={pref ? 'Wunsch-Teil – klicken zum Entfernen' : 'Als Wunsch-Teil markieren'} onClick={() => togglePref(a, p.id)}>
                              <Star size={13} color={pref ? 'var(--primary-color)' : 'var(--border-color)'} fill={pref ? 'var(--primary-color)' : 'none'} />
                            </button>
                            <span>{a.personName}</span>
                            <span className="qcodes">{a.qualifications.map((q) => q.code).join(',')}</span>
                            {a.bonus && <span className="badge badge-red" title={a.bonus_reason || ''} style={{ padding: '0 6px' }}>Bonus</span>}
                            <button className="icon-btn" title="Bonus / Notiz bearbeiten" onClick={() => setEditApp(a)} style={{ padding: 2 }}><Pencil size={12} /></button>
                            <button className="icon-btn" title="Aus diesem Teil entfernen" onClick={() => removeFromPart(p.id, a.person_id)} style={{ padding: 2 }}><X size={13} /></button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              <div className="divider" />
              <div className="flex between" style={{ marginBottom: 8 }}>
                <div className="small" style={{ fontWeight: 600 }}>Einteilung</div>
                <button className="btn btn-secondary btn-sm" onClick={() => setManual({ part: p })}><UserPlus size={14} /> Personen einteilen</button>
              </div>
              {list.length === 0 ? <div className="muted small">Noch niemand eingeteilt.</div> : (
                <div className="assign-grid">
                  {list.map((a) => (
                    <div className="slot filled" key={a.id} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                      <div className="flex between" style={{ gap: 6 }}>
                        <strong>{a.personName}</strong>
                        <button className="icon-btn" title="Entfernen" onClick={() => removeAssign(a.id)}><Trash2 size={14} /></button>
                      </div>
                      <div className="flex flex-wrap" style={{ gap: 4 }}>
                        <span className="badge badge-red">{ROLE_LABEL[a.role]}</span>
                        {a.source === 'manual' && <span className="badge badge-amber">manuell</span>}
                      </div>
                      {a.reason && <div className="muted xsmall">{a.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
              {!full && openSlots.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {openSlots.map((s, i) => (
                    <div className="slot open" key={i}>
                      <AlertTriangle size={15} color="var(--primary-color)" />
                      <span className="slot-role">{s.requirement}</span>
                      <span className="muted xsmall" style={{ flex: 1 }}>offen — {s.reason.replace(/^Unbesetzt[^:]*:\s*/, '')} Bitte passende Person nachmelden.</span>
                    </div>
                  ))}
                </div>
              )}
              {!full && openSlots.length === 0 && <div className="muted small" style={{ marginTop: 8 }}>{p.total_slots - list.length} Platz/Plätze offen — neu berechnen oder Person manuell einteilen.</div>}
            </div>
          );
        })}
      </div>

      {/* Bewerber */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-pad flex between">
          <div className="card-title"><Users2 size={18} /> {multi ? 'Bewerber-Übersicht' : 'Bewerber'} ({applications.length})</div>
          {!multi && <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}><Plus size={14} /> Bewerber hinzufügen</button>}
        </div>
        {applications.length === 0 ? <div className="empty"><Users2 size={36} /><p>Noch keine Bewerber ausgewählt.</p></div> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Name</th><th>Qualifikationen</th>{multi && <th>Verfügbar / Wunsch</th>}<th>Vorbelastung</th><th>Bonus</th><th>Ergebnis</th><th></th></tr></thead>
              <tbody>
                {applications.map((a) => {
                  const person = people.find((p) => p.id === a.person_id);
                  return (
                    <tr key={a.id}>
                      <td><strong>{a.personName}</strong>{a.decision_reason && <div className="muted xsmall" style={{ maxWidth: 320 }}>{a.decision_reason}</div>}</td>
                      <td><div className="tag-list">{a.qualifications.map((q) => <span key={q.id} className="qual-tag" title={q.name}>{q.code}</span>)}</div></td>
                      {multi && <td className="small">
                        {a.availablePartIds.map((pid) => {
                          const pref = a.preferredPartIds.includes(pid);
                          return <span key={pid} className={`badge ${pref ? 'badge-red' : 'badge-gray'}`} style={{ marginRight: 4 }}>{pref && <Star size={10} />}{partById[pid]?.label}</span>;
                        })}
                      </td>}
                      <td className="small">
                        <span className="muted">{person ? `${person.load.count} W · ${person.load.hours} h` : '—'}</span>
                        {a.passedOver > 0 && <span className="badge badge-amber" style={{ marginLeft: 4 }} title="zuvor beworben ohne Zusage">{a.passedOver}× übergangen</span>}
                      </td>
                      <td>{a.bonus ? <span className="badge badge-red" title={a.bonus_reason || ''}><Star size={11} /> Bonus</span> : <span className="muted">—</span>}</td>
                      <td>
                        {a.assignedInWatch ? <span className="badge badge-green">Eingeteilt</span>
                          : a.status ? <AppStatus s={a.status} />
                          : (watch.status === 'completed' ? <span className="badge badge-amber">übergangen</span> : <span className="badge badge-gray">offen</span>)}
                      </td>
                      <td className="row-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditApp(a)}><Pencil size={14} /></button>
                        <button className="btn btn-danger btn-sm" onClick={() => removeApp(a.id)}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transparenz / Begründung */}
      {decision && (
        <div className="card card-pad">
          <div className="card-title"><Scale size={18} /> Begründung & Transparenz</div>
          <div className="small muted" style={{ marginBottom: 12 }}>
            Letzter Lauf: {decision.createdAt} · {decision.summary.filledSlots}/{decision.summary.totalSlots} Plätze besetzt · {decision.summary.applicants} Bewerber.
            Für jeden Platz siehst du die Rangfolge der geeigneten Bewerber: Bonus zuerst, dann geringste Vorbelastung (Wachen, dann Stunden); bei Gleichstand kommt zuerst, wer sich früher beworben hatte, aber leer ausging; zuletzt der Wunsch-Teil.
          </div>
          {decision.parts.map((dp) => (
            <div key={dp.part_id} style={{ marginBottom: 14 }}>
              <strong>{dp.label || 'Teil'}{dp.time ? ` · ${dp.time}` : ''}</strong>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {dp.slots.map((slot, si) => {
                  const key = `${dp.part_id}-${si}`;
                  const open = openRank[key];
                  return (
                    <div className={`slot ${slot.filledBy ? 'filled' : 'open'}`} key={key} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                      <div className="flex between" style={{ cursor: 'pointer' }} onClick={() => setOpenRank({ ...openRank, [key]: !open })}>
                        <div className="flex" style={{ gap: 10 }}>
                          {slot.filledBy ? <CheckCircle2 size={16} color="var(--green)" /> : <AlertTriangle size={16} color="var(--primary-color)" />}
                          <span className="slot-role">{slot.requirement}</span>
                          <span>{slot.filledBy ? <strong>{slot.filledBy.name}</strong> : <span className="muted">unbesetzt</span>}</span>
                        </div>
                        <span className="flex small muted">{slot.ranking.filter((r) => r.eligible).length} geeignet {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
                      </div>
                      <div className="muted xsmall" style={{ marginLeft: 26 }}>{slot.reason}</div>
                      {open && (
                        <div className="rank-list" style={{ marginTop: 8, marginLeft: 26 }}>
                          {slot.ranking.map((r, ri) => (
                            <div className={`rank-row ${r.eligible ? 'elig' : 'inelig'}`} key={ri}>
                              {r.eligible ? <span className="rank-num">{r.rank}</span> : <span className="rank-num" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>–</span>}
                              <span style={{ flex: 1 }}>{r.name} {r.bonus && <Star size={11} color="var(--primary-color)" />} {r.prefersThis && <span className="badge badge-red" style={{ padding: '0 6px' }}>Wunsch</span>}</span>
                              <span className="muted xsmall">{r.load.count} W / {r.load.hours} h{r.passedOver > 0 ? ` · ${r.passedOver}× übergangen` : ''}{r.notes.length ? ` · ${r.notes.join(', ')}` : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Probleme & Hinweise – live nach manuellen Änderungen */}
      {assignments.length > 0 && (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <div className="card-title"><AlertTriangle size={18} /> Probleme & Hinweise{problems.length > 0 ? ` (${problems.filter((p) => p.severity === 'error').length} Fehler · ${problems.filter((p) => p.severity === 'warn').length} Warnungen)` : ''}</div>
          {problems.length === 0 ? (
            <div className="badge badge-green" style={{ marginTop: 8 }}><CheckCircle2 size={13} /> Keine Probleme – alles in Ordnung.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              {[...problems].sort((a, b) => (a.severity === 'error' ? 0 : a.severity === 'warn' ? 1 : 2) - (b.severity === 'error' ? 0 : b.severity === 'warn' ? 1 : 2)).map((pr, i) => {
                const col = pr.severity === 'error' ? 'var(--primary-color)' : pr.severity === 'warn' ? 'var(--amber)' : 'var(--text-secondary)';
                const bg = pr.severity === 'error' ? 'var(--red-bg)' : pr.severity === 'warn' ? 'var(--amber-bg)' : 'var(--bg-secondary)';
                return (
                  <div key={i} className="flex" style={{ gap: 8, padding: '8px 10px', borderRadius: 8, background: bg, alignItems: 'flex-start' }}>
                    {pr.severity === 'info' ? <Info size={15} color={col} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertTriangle size={15} color={col} style={{ flexShrink: 0, marginTop: 1 }} />}
                    <span className="small">{pr.message}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {addOpen && <AddCandidates watchId={id} parts={parts} people={people.filter((p) => !candidateIds.has(p.id))} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} toast={toast} />}
      {addPartFor && <AddToPart part={addPartFor} people={people} alreadyIds={new Set(applicantsForPart(addPartFor.id).map((a) => a.person_id))} onClose={() => setAddPartFor(null)} onDone={() => { setAddPartFor(null); load(); }} toast={toast} />}
      {editApp && <EditApplication app={editApp} parts={parts} onClose={() => setEditApp(null)} onDone={() => { setEditApp(null); load(); }} toast={toast} />}
      {manual && <ManualAssign part={manual.part} multi={multi} applications={applications} people={people}
        assignedIds={new Set(assignsByPart(manual.part.id).map((a) => a.person_id))}
        onClose={() => setManual(null)}
        onDone={(w) => { setManual(null); if (w && w.length) toast('Eingeteilt – Hinweise: ' + w.join('; '), 'err'); else toast('Eingeteilt'); load(); }} toast={toast} />}

      {relConfirm != null && <Confirm
        text={`Achtung: ${relConfirm}. Trotzdem freigeben?`}
        confirmLabel="Trotzdem freigeben" danger={false}
        onConfirm={doRelease} onClose={() => setRelConfirm(null)} />}

      {textOpen && (
        <Modal large title="Text für die Mitteilung" onClose={() => setTextOpen(false)}
          footer={<><button className="btn btn-secondary" onClick={() => setTextOpen(false)}>Schließen</button><button className="btn btn-primary" onClick={copyText}><Copy size={15} /> Kopieren</button></>}>
          <div className="small muted">Vorschau – „Kopieren" legt den Text in die Zwischenablage.</div>
          <textarea readOnly value={buildAssignmentText()} onFocus={(e) => e.target.select()} style={{ minHeight: 340, fontSize: 14, lineHeight: 1.5 }} />
        </Modal>
      )}
    </>
  );
}

/* ---------- Bewerber hinzufügen (Mehrfachauswahl) ---------- */
/* ---------- Bewerber zu EINEM Teil hinzufügen (additiv) ---------- */
function AddToPart({ part, people, alreadyIds, onClose, onDone, toast }) {
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState('');
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const selectable = people
    .filter((p) => !alreadyIds.has(p.id))
    .filter((p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.last_name.localeCompare(b.last_name));
  const save = async () => {
    if (!sel.length) return onClose();
    try { await api.post(`/parts/${part.id}/applicants`, { person_ids: sel }); toast(`${sel.length} Bewerber zu „${part.label}" hinzugefügt`); onDone(); }
    catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal title={`Bewerber für „${part.label}" hinzufügen`} onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Abbrechen</button><button className="btn btn-primary" onClick={save} disabled={sel.length === 0}>{sel.length} hinzufügen</button></>}>
      <div className="small muted">Wer sich für diesen Teil beworben hat, hier ankreuzen. Bereits erfasste Bewerber dieses Teils sind ausgeblendet. Mehrfachbewerbungen einfach in jedem Teil separat erfassen.</div>
      <input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="select-grid">
        {selectable.length === 0 && <div className="muted small">Keine weiteren Personen verfügbar.</div>}
        {selectable.map((p) => (
          <label key={p.id} className="select-tile" data-on={sel.includes(p.id)}>
            <input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggle(p.id)} />
            <span>{p.last_name}, {p.first_name}</span>
            <span className="qcodes">{p.qualifications.map((x) => x.code).join(',')}</span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

function AddCandidates({ watchId, parts, people, onClose, onDone, toast }) {
  const multi = parts.length > 1;
  const [sel, setSel] = useState([]);
  const [partSel, setPartSel] = useState(parts.map((p) => p.id)); // Standard: alle Teile
  const [q, setQ] = useState('');
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const togglePart = (id) => setPartSel(partSel.includes(id) ? partSel.filter((x) => x !== id) : [...partSel, id]);
  const filtered = people.filter((p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q.toLowerCase()));
  const save = async () => {
    if (!sel.length) return onClose();
    if (multi && partSel.length === 0) return toast('Mindestens einen Teil auswählen', 'err');
    const body = { person_ids: sel };
    if (multi) body.available_part_ids = partSel;
    try { await api.post(`/watches/${watchId}/applications/bulk`, body); toast(`${sel.length} Bewerber hinzugefügt`); onDone(); }
    catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal title="Bewerber hinzufügen" onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Abbrechen</button><button className="btn btn-primary" onClick={save}>{sel.length} hinzufügen</button></>}>
      {multi && (
        <div className="form-group">
          <label>Bewerbung für Teile</label>
          <div className="tag-list" style={{ gap: 8 }}>
            {parts.map((p) => {
              const on = partSel.includes(p.id);
              return (
                <button type="button" key={p.id} onClick={() => togglePart(p.id)} className="qual-tag"
                  style={{ cursor: 'pointer', padding: '6px 12px', border: on ? '1px solid var(--primary-color)' : '1px solid var(--border-color)', background: on ? 'var(--primary-light)' : '#fff', color: on ? 'var(--primary-dark)' : 'var(--text-secondary)' }}>
                  {on ? '✓ ' : ''}{p.label}
                </button>
              );
            })}
          </div>
          <span className="muted xsmall">Gilt für alle hier ausgewählten Personen. Wunsch-Teil/Bonus später je Person per ✎ verfeinern.</span>
        </div>
      )}
      <input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
      {!multi && <div className="small muted">Verfügbarkeit & Bonus können danach je Bewerber gesetzt werden.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 320, overflowY: 'auto' }}>
        {filtered.length === 0 && <div className="muted small">Alle aktiven Personen sind bereits Bewerber.</div>}
        {filtered.map((p) => (
          <label key={p.id} className="checkbox-row" style={{ padding: '8px 10px', borderRadius: 8, background: sel.includes(p.id) ? 'var(--primary-light)' : 'transparent' }}>
            <input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggle(p.id)} />
            <span style={{ flex: 1 }}>{p.last_name}, {p.first_name}</span>
            <span className="tag-list">{p.qualifications.map((x) => <span key={x.id} className="qual-tag">{x.code}</span>)}</span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

/* ---------- Bewerbung bearbeiten (Verfügbarkeit / Wunsch / Bonus) ---------- */
function EditApplication({ app, parts, onClose, onDone, toast }) {
  const multi = parts.length > 1;
  const [avail, setAvail] = useState(app.availablePartIds.length ? app.availablePartIds : parts.map((p) => p.id));
  const [prefs, setPrefs] = useState(app.preferredPartIds || []);
  const [bonus, setBonus] = useState(app.bonus);
  const [bonusReason, setBonusReason] = useState(app.bonus_reason || '');
  const [note, setNote] = useState(app.note || '');

  const toggleAvail = (pid) => {
    const has = avail.includes(pid);
    setAvail(has ? avail.filter((x) => x !== pid) : [...avail, pid]);
    if (has) setPrefs(prefs.filter((x) => x !== pid)); // nicht mehr verfügbar → auch kein Wunsch
  };
  const togglePrefPart = (pid) => setPrefs(prefs.includes(pid) ? prefs.filter((x) => x !== pid) : [...prefs, pid]);
  const save = async () => {
    try {
      await api.put(`/applications/${app.id}`, {
        available_part_ids: avail, preferred_part_ids: prefs.filter((x) => avail.includes(x)),
        bonus, bonus_reason: bonus ? bonusReason : null, note,
      });
      toast('Gespeichert'); onDone();
    } catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal title={`Bewerber: ${app.personName}`} onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Abbrechen</button><button className="btn btn-primary" onClick={save}>Speichern</button></>}>
      {multi && <>
        <div className="form-group"><label>Verfügbar für Teile</label>
          <div className="tag-list" style={{ gap: 8 }}>
            {parts.map((p) => (
              <label key={p.id} className="checkbox-row" style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: '6px 10px' }}>
                <input type="checkbox" checked={avail.includes(p.id)} onChange={() => toggleAvail(p.id)} /> {p.label}
              </label>
            ))}
          </div>
        </div>
        <div className="form-group"><label>Wunsch-Teile (Präferenz, mehrere möglich)</label>
          <div className="tag-list" style={{ gap: 8 }}>
            {parts.filter((p) => avail.includes(p.id)).map((p) => {
              const on = prefs.includes(p.id);
              return (
                <button type="button" key={p.id} onClick={() => togglePrefPart(p.id)} className="qual-tag"
                  style={{ cursor: 'pointer', padding: '6px 12px', border: on ? '1px solid var(--primary-color)' : '1px solid var(--border-color)', background: on ? 'var(--primary-light)' : '#fff', color: on ? 'var(--primary-dark)' : 'var(--text-secondary)' }}>
                  {on ? '★ ' : '☆ '}{p.label}
                </button>
              );
            })}
            {parts.filter((p) => avail.includes(p.id)).length === 0 && <span className="muted small">Erst Teile als „verfügbar" wählen.</span>}
          </div>
          <span className="muted xsmall">Markierte Teile werden bevorzugt zugeteilt. Mehrere möglich (z. B. „Samstag morgen oder abend").</span>
        </div>
      </>}
      <label className="checkbox-row"><input type="checkbox" checked={bonus} onChange={(e) => setBonus(e.target.checked)} /> <Star size={14} /> Bevorzugung (Bonus)</label>
      {bonus && <div className="form-group"><label>Grund für die Bevorzugung</label><textarea value={bonusReason} onChange={(e) => setBonusReason(e.target.value)} placeholder="z. B. besondere Zusage, Ausgleich, …" /></div>}
      <div className="form-group"><label>Notiz</label><input value={note} onChange={(e) => setNote(e.target.value)} /></div>
    </Modal>
  );
}

/* ---------- Manuelle Einteilung: Mehrfachauswahl per Häkchen + Funktion ---------- */
function ManualAssign({ part, multi, applications, people, assignedIds, onClose, onDone, toast }) {
  const specials = [];
  if (part.requires_gf) specials.push('GF');
  if (part.requires_ma) specials.push('Ma');
  const [sel, setSel] = useState({}); // person_id -> Rolle
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  const candidateIds = new Set(applications.map((a) => a.person_id));
  const selectable = people
    .filter((p) => !assignedIds.has(p.id))
    .filter((p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (candidateIds.has(b.id) - candidateIds.has(a.id)) || a.last_name.localeCompare(b.last_name));
  const selIds = Object.keys(sel).map(Number);
  const nameOf = (id) => { const p = people.find((x) => x.id === id); return p ? p.last_name : id; };

  const toggle = (id) => setSel((prev) => { const n = { ...prev }; if (id in n) delete n[id]; else n[id] = 'CREW'; return n; });
  const setRole = (id, role) => setSel((prev) => ({ ...prev, [id]: role }));

  const save = async () => {
    if (!selIds.length) return onClose();
    setSaving(true);
    const warns = [];
    try {
      for (const pid of selIds) {
        const r = await api.post(`/parts/${part.id}/assignments`, { person_id: pid, role: sel[pid] });
        if (r && r.warnings) warns.push(...r.warnings.map((w) => `${nameOf(pid)}: ${w}`));
      }
      onDone(warns);
    } catch (e) { setSaving(false); toast(e.message, 'err'); }
  };

  return (
    <Modal large title={`Personen einteilen${multi ? ` · ${part.label}` : ''}`} onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || selIds.length === 0}>{saving ? 'Speichern…' : `${selIds.length} einteilen`}</button></>}>
      <div className="info-box"><Info size={13} style={{ verticalAlign: -2 }} /> Personen ankreuzen, dann ggf. Sonderfunktion zuweisen. Standard ist „Mannschaft". Hinweise (z. B. fehlende Qualifikation) werden gemeldet, die Einteilung aber gesetzt.</div>
      <input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="select-grid">
        {selectable.length === 0 && <div className="muted small">Keine weiteren Personen verfügbar.</div>}
        {selectable.map((p) => (
          <label key={p.id} className="select-tile" data-on={p.id in sel}>
            <input type="checkbox" checked={p.id in sel} onChange={() => toggle(p.id)} />
            <span>{p.last_name}, {p.first_name}</span>
            <span className="qcodes">{p.qualifications.map((x) => x.code).join(',')}</span>
          </label>
        ))}
      </div>
      {selIds.length > 0 && (specials.length > 0 ? (
        <div>
          <label style={{ display: 'block', marginBottom: 4 }}>Funktion ({selIds.length} ausgewählt · Standard: Mannschaft)</label>
          {selIds.map((pid) => (
            <div className="role-row" key={pid}>
              <span>{nameOf(pid)}, {(people.find((x) => x.id === pid) || {}).first_name}</span>
              <select value={sel[pid]} onChange={(e) => setRole(pid, e.target.value)}>
                <option value="CREW">Mannschaft</option>
                {specials.includes('GF') && <option value="GF">Wachführer (GF)</option>}
                {specials.includes('Ma') && <option value="Ma">Maschinist (Ma)</option>}
              </select>
            </div>
          ))}
        </div>
      ) : <div className="muted small">{selIds.length} Person(en) ausgewählt – werden als Mannschaft eingeteilt.</div>)}
    </Modal>
  );
}
